import axios from 'axios';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';

import {
  $cartState,
  SelectedProduct,
  handleAddProductInCart,
  handleUpdateCartItemCount,
} from '../../../store/models/cart';
import Button from '../../forms/Button/Button';
import { checkoutCart } from '../../../helpers/checkout';
import { hasFrequencyConflict, persistCartFromStore } from '../../../helpers/cart';
import useToaster from '../../../hooks/useToaster';
import BackButton from '../../forms/Button/BackButton';
import { openCartSidebar } from '../../../store/store';
import { EProducts, EProductSizes } from '../../../model/enums';
import { $productsState } from '../../../store/models/products';
import { trackAddToCart } from '../../../analytics/ecommerce';

import styles from './styles.module.scss';

type Props = {
  page: 1 | 2 | 3;
  changeNextPage: () => void;
  changePrevPage: () => void;
};

const SliderControlBar = ({ page, changeNextPage, changePrevPage }: Props) => {
  const { t } = useTranslation();
  const {
    selectedProduct,
    selectedProductSize,
    selectedProductFrequency,
    isSelectedAccessory
  } = useUnit($cartState);
  const {accessories} = useUnit($productsState)

  const { errorMessage } = useToaster();

  async function handleCheckout() {
    // Add product to cart if it's not already there
    if (!selectedProduct || !selectedProductSize || !selectedProductFrequency) {
      console.error('Missing required fields to add product to cart.');
      errorMessage('Missing required fields to add product to cart.');
      return;
    }

    const prod = {
      productType: EProducts.grs,
      size: selectedProductSize as EProductSizes,
      data: selectedProduct,
      frequency: selectedProductFrequency as
        | 'week_price'
        | 'two_week_price'
        | 'three_week_price'
        | 'four_week_price'
        | 'one_off_price',
      // The wizard has no quantity picker — always add one grass. `selectedProductCount`
      // may be stale (e.g. catnip set it to 20 on a previous page) so don't read it here.
      count: 1,
    };

    // Read the cart from the Effector store (source of truth), not localStorage,
    // so we don't mutate a stale copy or persist mismatched indexes.
    let cart = $cartState.getState().cartState;

    // One subscription cadence per cart so a single Checkout session can bill it all.
    if (hasFrequencyConflict(cart, prod.frequency)) {
      errorMessage(
        'Your cart already has a different subscription frequency. Finish that checkout first or change the existing item.',
      );
      return;
    }

    const existingProductIndex = cart.findIndex(
      (item: SelectedProduct) =>
        item.size === prod.size &&
        item.frequency === prod.frequency &&
        JSON.stringify(item.data) === JSON.stringify(prod.data),
    );

    if (existingProductIndex !== -1) {
      handleUpdateCartItemCount({
        index: existingProductIndex,
        count: cart[existingProductIndex].count + prod.count,
      });
    } else {
      handleAddProductInCart(prod);
    }
    const addedItems: SelectedProduct[] = [prod];

    // The tray is sold per grass size — match it to the selection instead of
    // assuming the standard one.
    const selectedAccessory = accessories.find(
      (ac) => ac.size === selectedProductSize,
    );

    if (isSelectedAccessory && selectedAccessory) {
      const accessoryData = {
        productType: EProducts.acs,
        size: selectedProductSize as EProductSizes,
        data: selectedAccessory,
        frequency: 'one_off_price',
        count: 1,
      };

      // Re-read the cart after the grass dispatch so indexes are fresh.
      cart = $cartState.getState().cartState;
      const existingAccessoryIndex = cart.findIndex(
        (item: SelectedProduct) =>
          item.size === accessoryData.size &&
          item.frequency === accessoryData.frequency &&
          JSON.stringify(item.data) === JSON.stringify(accessoryData.data),
      );

      if (existingAccessoryIndex !== -1) {
        handleUpdateCartItemCount({
          index: existingAccessoryIndex,
          count: cart[existingAccessoryIndex].count + 1,
        });
      } else {
        // @ts-ignore
        handleAddProductInCart(accessoryData);
      }
      // @ts-ignore -- same shape the cart store accepts above
      addedItems.push(accessoryData);
    }

    persistCartFromStore();
    trackAddToCart(addedItems);

    // Build the checkout payload from the final store state.
    const products = $cartState.getState().cartState.map((item) => ({
      price: (item.data as Record<string, any>)[item.frequency],
      quantity: item.count,
      frequency: item.frequency,
    }));

    try {
      await checkoutCart(products);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios Error:', error.response?.data || error.message);
      } else if (error instanceof Error) {
        console.error('Error:', error.message);
      } else {
        console.error('Unexpected Error:', error);
      }
    }
  }

  return (
    <div className={styles.sliderControlBar}>
      <div className={styles.btnsWrapper}>
        {(page === 2 || page === 3) && <BackButton onClick={changePrevPage} />}
        {(page === 1 || page === 2) && (
          <Button
            onClick={changeNextPage}
            disabled={page === 1 && !selectedProductSize}
          >
            {t('continue')}
          </Button>
        )}
        {page === 3 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Button
              style={{ width: '100%', minWidth: 'fit-content' }}
              onClick={handleCheckout}
            >
              {t('proceedToCheckout')}
            </Button>
            <Button
              btnType="outlined"
              style={{ width: '100%' }}
              onClick={() => openCartSidebar()}
            >
              {t('goToCart')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SliderControlBar;
